import React, { useEffect, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";

/**
 * StarField
 * Renders random white dots that softly flash in/out.
 *
 * Props:
 *  - count?: number        (default 80)
 *  - maxSize?: number      (px, default 2)
 *  - areaSelector?: string (optional CSS selector to measure instead of window)
 *  - zIndex?: number       (default 0)
 */
export default function StarField({
  count = 80,
  maxSize = 2,
  areaSelector,
  zIndex = 0,
}) {
  const shouldReduceMotion = useReducedMotion();
  const measureRef = useRef(null);

  // Measure the container (or window) so stars fill the visible area.
  useEffect(() => {
    if (!areaSelector) return;
    measureRef.current = document.querySelector(areaSelector);
  }, [areaSelector]);

  const dims = () => {
    const el = measureRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      return { w: r.width, h: r.height };
    }
    return { w: window.innerWidth, h: window.innerHeight };
  };

  // Precompute random positions, sizes, and timings deterministically per mount.
  const stars = useMemo(() => {
    const { w, h } = dims();
    return Array.from({ length: count }, (_, i) => {
      const x = Math.random() * w;
      const y = Math.random() * (h * 0.7);
      const size = 1 + Math.random() * (maxSize - 1);
      const delay = Math.random() * 3; // seconds
      const dur = 1.5 + Math.random() * 2.5; // seconds
      const opacityBase = 0.2 + Math.random() * 0.4;
      return { id: i, x, y, size, delay, dur, opacityBase };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, maxSize]);

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex,
      }}
    >
      {stars.map((s) => (
        <motion.div
          key={s.id}
          style={{
            position: "absolute",
            left: s.x,
            top: s.y,
            width: s.size,
            height: s.size,
            borderRadius: "50%",
            backgroundColor: "#fff",
            opacity: shouldReduceMotion ? 0.8 : s.opacityBase,
            filter: "drop-shadow(0 0 2px rgba(255,255,255,.6))",
          }}
          animate={
            shouldReduceMotion
              ? undefined
              : {
                  opacity: [s.opacityBase, 1, s.opacityBase],
                }
          }
          transition={
            shouldReduceMotion
              ? undefined
              : {
                  duration: s.dur,
                  delay: s.delay,
                  repeat: Infinity,
                  ease: "easeInOut",
                }
          }
        />
      ))}
    </Box>
  );
}
